import type { NetworkTelemetryMeta, NetworkTelemetryRow } from "@features/reports/api";
import { buildTrend } from "@shared/buildTrend";
import dayjs from "dayjs";
import { useMemo } from "react";

import type { CoefficientMode, NetworkTelemetryMetric } from "./store";

export type NetworkTelemetryChartPoint = {
  time: string;
  timestamp: string;
  value?: number | null;
  trend?: number | null;
  trendUpper?: number | null;
  trendLower?: number | null;
};

export const useNetworkTelemetryChartModel = ({
  rows,
  metric,
  coefficient,
  meta,
}: {
  rows: NetworkTelemetryRow[];
  metric: NetworkTelemetryMetric;
  coefficient: CoefficientMode;
  meta?: NetworkTelemetryMeta;
}) => {
  return useMemo(() => {
    const metricLabel = metric === "kw" ? "kW" : metric === "kvar" ? "kvar" : "I";
    const isScaled = coefficient === "with";

    const pickValue = (row: NetworkTelemetryRow): number | null | undefined => {
      if (metric === "kvar") return isScaled ? row.kvar : row.kvar_raw;
      if (metric === "i") return isScaled ? row.i : row.i_raw;
      return isScaled ? row.kw : row.kw_raw;
    };

    // Backend trend available only for kW scaled
    const useBackendTrend = metric === "kw" && isScaled && rows.some((r) => r.trend_kw != null);

    const points = rows.map((row) => ({
      x: dayjs(row.timestamp).valueOf(),
      time: dayjs(row.timestamp).format("DD.MM HH:mm"),
      timestamp: row.timestamp,
      value: pickValue(row),
      valueNumeric:
        typeof pickValue(row) === "number" && Number.isFinite(pickValue(row))
          ? (pickValue(row) as number)
          : undefined,
      trendKW: row.trend_kw,
      trendUpperKW: row.trend_upper_kw,
      trendLowerKW: row.trend_lower_kw,
    }));

    let chartData: NetworkTelemetryChartPoint[];
    let hasTrend = false;
    let trendPerHour: number | null | undefined;

    if (useBackendTrend) {
      chartData = points.map((p) => ({
        time: p.time,
        timestamp: p.timestamp,
        value: p.value,
        trend: p.trendKW,
        trendUpper: p.trendUpperKW,
        trendLower: p.trendLowerKW,
      }));
      hasTrend = true;
      trendPerHour = meta?.trend_slope_kw;
    } else {
      const modelPoints = points
        .map((p) => ({ x: p.x, y: p.valueNumeric }))
        .filter(
          (p): p is { x: number; y: number } => typeof p.y === "number" && Number.isFinite(p.y),
        );

      const model = buildTrend(modelPoints);
      chartData = points.map((p) => ({
        time: p.time,
        timestamp: p.timestamp,
        value: p.value,
        trend: model ? model.intercept + model.slope * p.x : undefined,
      }));
      hasTrend = Boolean(model);
      trendPerHour = model ? model.slope * 60 * 60 * 1000 : undefined;
    }

    const seriesValues = chartData
      .map((p) => p.value)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));

    const max = seriesValues.length > 0 ? Math.max(...seriesValues) : undefined;
    const last = seriesValues.length > 0 ? seriesValues[seriesValues.length - 1] : undefined;

    return { metricLabel, chartData, hasTrend, max, last, trendPerHour, isScaled };
  }, [metric, rows, coefficient, meta]);
};
